import {
  useQuery,
  QueryObserverResult,
  RefetchOptions,
} from '@tanstack/react-query';
import { fetchCompanyMemberNotifications } from '@company/services/notifications/api';
import ICompanyMemberIndicators from '@th-types/company-member-indicators.type';

const CACHE_KEY_DATA = 'notificationIndicators';
const CACHE_KEY_TIMESTAMP = 'notificationIndicatorsTimestamp';
const CACHE_DURATION_MINUTES = 5;

/**
 * Clears the company indicators cache from localStorage
 */
export const clearCompanyIndicatorsCache = (): void => {
  localStorage.removeItem(CACHE_KEY_DATA);
  localStorage.removeItem(CACHE_KEY_TIMESTAMP);
};

/**
 * Hook for fetching and caching company notification indicators
 */
export const useCompanyIndicators = () => {
  const { data, refetch, isLoading, error } = useQuery({
    queryKey: ['companyNotifications'],
    queryFn: async () => {
      // Check if we have cached data and it's still valid
      const cachedData = localStorage.getItem(CACHE_KEY_DATA);
      const cachedTimestamp = localStorage.getItem(CACHE_KEY_TIMESTAMP);

      if (cachedData && cachedTimestamp) {
        const now = new Date().getTime();
        const cacheTime = parseInt(cachedTimestamp, 10);
        const cacheAgeMinutes = (now - cacheTime) / (1000 * 60);

        // If cache is less than CACHE_DURATION_MINUTES (5) minutes old, use it
        if (cacheAgeMinutes < CACHE_DURATION_MINUTES) {
          return JSON.parse(cachedData);
        }
      }

      // Cache is invalid or expired, fetch fresh data
      try {
        const notificationData = await fetchCompanyMemberNotifications();

        // Cache the response
        localStorage.setItem(CACHE_KEY_DATA, JSON.stringify(notificationData));
        localStorage.setItem(
          CACHE_KEY_TIMESTAMP,
          new Date().getTime().toString()
        );

        return notificationData;
      } catch (_error) {
        // If we have cached data, use it even if expired
        if (cachedData) {
          return JSON.parse(cachedData);
        }
        throw _error;
      }
    },
    staleTime: CACHE_DURATION_MINUTES * 60 * 1000,
  });

  const fetchNotifications = (
    options?: RefetchOptions
  ): Promise<QueryObserverResult<ICompanyMemberIndicators, Error>> => {
    return refetch(options);
  };

  return {
    indicators: data,
    fetchNotifications,
    isLoading,
    error,
  };
};

export default useCompanyIndicators;
